import React, { useState } from 'react';
import Modal from '../Modal/Modal';
import './AntecedentsArea.css';
import { INSPECTION_STATUS_VERDICT } from '../../utils/constants';
import PlacApiResults from '../PlacApiResults';
import OfficialRecordsConfirmModal from '../OfficialRecordsConfirmModal/OfficialRecordsConfirmModal';
import config from '../../config/environment';

const { PENDING, IN_PROGRESS, APPROVED, REJECTED } = INSPECTION_STATUS_VERDICT;

const AntecedentsArea = ({ 
    antecedents, 
    onAntecedentsChange, 
    antecedentsComments, 
    onCommentsChange, 
    state = PENDING,
    onStart,
    onApprove,
    onReject,
    isComplete = false,
    enableApproveRejectButtons = false,
    enableStartButton = false,
    rejectedPhotosCount = 0,
    placaApi = null,
    vehicleData = {},
    contactData = {},
    onRetryPlacApi,
    showTechnicalError = false,
    showPlacApiFields = false,
}) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [showOfficialRecordsModal, setShowOfficialRecordsModal] = useState(false);
    const [officialRecordsMode, setOfficialRecordsMode] = useState('start');
    const [officialRecordsConsultBy, setOfficialRecordsConsultBy] = useState('document');

    const defaultConsultBy = String(contactData.docType || '').trim() && String(contactData.docNumber || '').trim()
        ? 'document'
        : 'vin';
    const vehicleVin = vehicleData.vin || vehicleData.chassis || '';

    const handleAntecedentsClick = (value) => {
        if (state === IN_PROGRESS) {
            onAntecedentsChange(value);
        }
    };

    const handleCommentsChange = (e) => {
        if (state === IN_PROGRESS) {
            onCommentsChange(e.target.value);
        }
    };

    const handleApproveClick = () => {
        if (state === IN_PROGRESS && isComplete) {
            setConfirmAction(APPROVED);
            setShowConfirmModal(true);
        }
    };

    const handleRejectClick = () => {
        if (state === IN_PROGRESS && isComplete) {
            setConfirmAction(REJECTED);
            setShowConfirmModal(true);
        }
    };

    const handleConfirmAction = () => {
        if (confirmAction === APPROVED) {
            onApprove();
        } else if (confirmAction === REJECTED) {
            onReject();
        }
        setShowConfirmModal(false);
        setConfirmAction(null);
    };

    const handleCancelModal = () => {
        setShowConfirmModal(false);
        setConfirmAction(null);
    };

    const openOfficialRecordsModal = (mode, consultBy = defaultConsultBy) => {
        setOfficialRecordsMode(mode);
        setOfficialRecordsConsultBy(consultBy);
        setShowOfficialRecordsModal(true);
    };

    const handleConfirmOfficialRecords = (values) => {
        setShowOfficialRecordsModal(false);
        if (officialRecordsMode === 'retry' && onRetryPlacApi) {
            onRetryPlacApi(values);
            return;
        }
        onStart(values);
    };
    
    const isDisabled = !enableApproveRejectButtons;

    return (
        <div className="antecedents-area">
            {showPlacApiFields && (config.ENABLE_OFFICIAL_RECORDS || placaApi?.data) && (state === IN_PROGRESS || placaApi) && (
                <PlacApiResults
                    placaApi={placaApi}
                    vehicleData={vehicleData}
                    onRetry={() => openOfficialRecordsModal('retry', placaApi?.error?.consultBy || defaultConsultBy)}
                    onConsultByVin={() => openOfficialRecordsModal('retry', 'vin')}
                    showRetry={config.ENABLE_OFFICIAL_RECORDS && enableApproveRejectButtons && Boolean(onRetryPlacApi)}
                    showTechnicalError={showTechnicalError}
                />
            )}

            <div className="antecedents-items-container">
                <div className="antecedents-item">
                    <div className="item-label">
                        <span className="item-text">Antecedentes</span>
                        <span className="required-indicator">*</span>
                    </div>
                    <div className="item-options">
                        <button
                            className={`option-button ${antecedents === true ? 'selected' : ''}`}
                            onClick={() => handleAntecedentsClick(true)}
                            disabled={isDisabled}
                        >
                            SI
                        </button>
                        <button
                            className={`option-button ${antecedents === false ? 'selected' : ''}`}
                            onClick={() => handleAntecedentsClick(false)}
                            disabled={isDisabled}
                        >
                            NO
                        </button>
                    </div>
                </div>
            </div>

            <div className="antecedents-footer">
                <div className="comments-section">
                    <label htmlFor="antecedents-comments" className="comments-label">
                        Observaciones
                    </label>
                    <textarea
                        id="antecedents-comments"
                        className="comments-textarea"
                        value={antecedentsComments}
                        onChange={handleCommentsChange}
                        placeholder="Escribe tus observaciones sobre antecedentes aquí..."
                        rows={6}
                        disabled={isDisabled}
                    />
                </div>

                <div className="verdict-actions">
                    {enableStartButton && (
                        <button 
                            className="start-button"
                            onClick={() => {
                                if (config.ENABLE_OFFICIAL_RECORDS) {
                                    openOfficialRecordsModal('start');
                                    return;
                                }
                                onStart();
                            }}
                        >
                            Iniciar
                        </button>
                    )}
                    
                    {enableApproveRejectButtons && (
                        <div className="final-verdict-buttons">
                            <button
                                className="antecedents-button antecedents-pass-button"
                                onClick={handleApproveClick}
                                disabled={!isComplete}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 12l2 2 4-4"></path>
                                    <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                                </svg>
                                Aprobar
                            </button>
                            <button
                                className="antecedents-button antecedents-reject-button"
                                onClick={handleRejectClick}
                                disabled={!isComplete}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18"></path>
                                    <path d="M6 6l12 12"></path>
                                </svg>
                                Rechazar
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de confirmación */}
            <Modal
                isOpen={showConfirmModal}
                onClose={handleCancelModal}
                title={`Confirmar ${confirmAction === APPROVED ? 'Aprobación' : 'Rechazo'}`}
            >
                <div className="modal-content">
                    <p>
                        ¿Estás seguro de que deseas {confirmAction === APPROVED ? 'aprobar' : 'rechazar'} el área de antecedentes?
                    </p>
                    
                    {(rejectedPhotosCount > 0) && (
                        <div className="modal-warning">
                            <p><strong>Advertencia:</strong></p>
                            <p>
                                Existen {rejectedPhotosCount} foto(s) rechazada(s).
                            </p>
                            <p>
                                Al finalizar la inspección, las fotos pendientes de revisión se aprobarán automáticamente.
                            </p>
                        </div>
                    )}
                    
                    <p className="modal-warning">
                        Esta acción no se puede deshacer.
                    </p>
                    <div className="modal-actions">
                        <button 
                            className="modal-button cancel-button"
                            onClick={handleCancelModal}
                        >
                            Cancelar
                        </button>
                        <button 
                            className={`modal-button ${confirmAction === APPROVED ? 'antecedents-approve-button' : 'antecedents-reject-button'}`}
                            onClick={handleConfirmAction}
                        >
                            {confirmAction === APPROVED ? 'Aprobar' : 'Rechazar'}
                        </button>
                    </div>
                </div>
            </Modal>
            <OfficialRecordsConfirmModal
                isOpen={showOfficialRecordsModal}
                onClose={() => setShowOfficialRecordsModal(false)}
                onConfirm={handleConfirmOfficialRecords}
                initialConsultBy={officialRecordsConsultBy}
                initialValues={{
                    placa: vehicleData.licensePlate || '',
                    docType: contactData.docType || '',
                    docNumber: contactData.docNumber || '',
                    vin: vehicleVin,
                }}
            />
        </div>
    );
};

export default AntecedentsArea;